import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { ActionLink, Input, Submit } from "@wine2trace/ui/FormComponents";
import useLabels from "../../hooks/useLabels";
import api from "../../api/axios";
import { requestPasswordReset } from "../../services/utils";
import { EMAIL_REGEX } from "../../constants/regex";
import useLoginPageClass from "./useLoginPageClass";
import "./styles/index.scss";

// Public page (no session): one e-mail field, then always the same neutral confirmation. The server never says
// whether the address belongs to an account, and neither does this page: success and "not found" look the same.
// Only a failure that says nothing about the account (network, 5xx, rate limit) is reported as an error.
const ForgotPassword = () => {
  useLoginPageClass();
  const [getLabel] = useLabels();
  const [sent, setSent] = useState(false);
  const sending = useRef(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { email: "" }, mode: "onTouched" });

  const onSubmit = async ({ email }) => {
    if (sending.current) return;
    sending.current = true;
    try {
      await requestPasswordReset(email.trim(), api);
      setSent(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error(getLabel("passwordResetRateLimited"), { position: toast.POSITION.BOTTOM_RIGHT });
      } else if (!status || status >= 500) {
        toast.error(getLabel("passwordResetGenericError"), { position: toast.POSITION.BOTTOM_RIGHT });
      } else {
        // 404 and the other 4xx: same screen as a real success.
        setSent(true);
      }
    } finally {
      sending.current = false;
    }
  };

  return (
    <div className="login login_wrapper red_card password_recovery">
      <h1>{getLabel("forgotPasswordTitle")}</h1>
      {sent ? (
        <p className="password_recovery_notice" role="status">
          {getLabel("forgotPasswordSent")}
        </p>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate>
          <p className="password_recovery_text">{getLabel("forgotPasswordIntro")}</p>
          <Input
            name="email"
            register={register}
            // Trimmed first: keyboards and autofill often add a trailing space.
            validate={(value) => EMAIL_REGEX.test(value.trim()) || getLabel("inputPatternError")}
            type="text"
            label={getLabel("email")}
            placeholder={getLabel("emailPlaceholder")}
            error={errors["email"]}
            required={getLabel("inputRequiredError")}
            isRequiredTooltip={false}
          />
          <Submit customClassName="is_login" label={getLabel("forgotPasswordSubmit")} />
        </form>
      )}
      <div className="login_footer">
        <ActionLink to="/login">{getLabel("backToLogin")}</ActionLink>
      </div>
    </div>
  );
};

export default ForgotPassword;
